/**
 * Story 12.28 — the one definition of "safe to publish" for a listing's location.
 *
 * The wizard collects the seller's exact address and the exact coordinate of the
 * place they picked. Both are needed (operator ruling R2: the exact address stays
 * private to TPH), and neither may reach the public payload (R1: buyers see the
 * state or province only; R5: the stored coordinate is coarsened to the 0.1°
 * grid).
 *
 * 🔴 OPERATOR RULING, 2026-09-18 — SCOPE CUT. R1 was "city and state"; it is now
 * THE STATE OR PROVINCE ALONE. The listing page already displays the state
 * alone, and no buyer- or seller-facing surface displays the town (checked
 * surface by surface, and against the live served HTML, before the cut). Keeping
 * the town is what had brought in a coordinate→place lookup service, a second
 * map token, a shared rate limit with sellers' address suggestions, and a ruling
 * on listings whose town and coordinate disagreed. With the town gone, a public
 * address is a canonical region name and nothing else, so there is no longer
 * anything a string cannot certify and nothing to look up.
 *
 * 🔴 Why coarsening matters although no map renders the point (R5): the pair is
 * delivered inside the public listing payload and appears in the listing page's
 * preloaded state, so anyone who views source or calls the public Marketplace API
 * reads it. Nothing has to draw it for it to be read.
 *
 * 🔴 OPERATOR RULING, 2026-09-18 — POSITIVE CONSTRUCTION ONLY.
 *
 * The first build of this module decided whether a string was a street address
 * by testing it against a closed list of street-type words plus a leading-digit
 * check. That cannot be made safe, and it produced three separate defects in one
 * review (marketplace HIGH-1, tph-api C2 and H1):
 *
 *   - the wordlist is open-ended, so `Windmill Draw`, `Larkspur Bend` and
 *     `Cedar Bluff Crossing` were published verbatim AND certified safe;
 *   - `\bst\b` matched the `St.` in `St. Louis`, so 13 of 21 real towns lost
 *     their city — and an already-safe address was "repaired" into something
 *     worse;
 *   - a bare `'ca'` in the country list shadowed California's postal code, so
 *     `Paso Robles, CA` lost its address entirely.
 *
 * It is the CLAUDE.md class: asserting a NAME that usually accompanies the
 * property instead of the property. "Contains no street-type word" is a name.
 * "Is a populated place" is the property, and no wordlist can establish it.
 *
 * THE RULE THIS MODULE NOW ENFORCES:
 *
 *   🔴 THE PUBLIC ADDRESS IS A CANONICAL REGION NAME, CONSTRUCTED HERE. There
 *      is no blacklist and no "doesn't look like a street" test, and no segment
 *      of the formatted string is ever published. The region is resolved by
 *      EQUALITY against a closed, authoritative catalogue, and the catalogue's
 *      own spelling is what goes out.
 *
 *   🔴 ON A FRESH PICK, THE REGION COMES FROM THE GEOCODER'S STRUCTURED REGION
 *      CODE (`buildPublicAddress`). That is what settles District of Columbia
 *      against Washington state, and California against Quebec. Everywhere else
 *      — the backfill, the server backstop, a pick without structured values —
 *      it comes from the string (`toPublicAddress`).
 *
 *   🔴 IF NO REGION RESOLVES, PUBLISH NO ADDRESS. The public location key is
 *      CLEARED — sent as `null`, never omitted (ruling R13). See
 *      `sanitizeListingLocation` for why, and for what it costs the wizard's
 *      completion check.
 *
 *   🔴 NEVER GUESS A REGION. Where the string cannot settle it — a trailing
 *      country that contradicts it, a postal code from the other country — no
 *      region is published rather than a wrong one (cold review 2026-09-21).
 *
 * This module is PURE — no imports, no SDK, no config, no network — so the same
 * predicates run in the browser save path, in a Jest test, and (as a behavioural
 * twin) in tph-api's `src/services/locationPrivacy.js`, which the events-poller
 * backstop and the backfill use. The twin mirrors the `stateProvince.js` ↔
 * `deriveListingFields.js` precedent already in this codebase.
 */

// Canonical region catalogues. A region is identified by EQUALITY against one of
// these — its full name, its two-letter postal code, or its ISO 3166-2 code —
// never by position in a string and never by what it looks like.
const US_STATES = {
  AL: 'Alabama',
  AK: 'Alaska',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DC: 'District of Columbia',
  DE: 'Delaware',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
};

const CA_PROVINCES = {
  AB: 'Alberta',
  BC: 'British Columbia',
  MB: 'Manitoba',
  NB: 'New Brunswick',
  NL: 'Newfoundland and Labrador',
  NS: 'Nova Scotia',
  NT: 'Northwest Territories',
  NU: 'Nunavut',
  ON: 'Ontario',
  PE: 'Prince Edward Island',
  QC: 'Quebec',
  SK: 'Saskatchewan',
  YT: 'Yukon',
};

// 🔴 `DC` is present in US_STATES above, and that is deliberate. The 12-2 review
// (NIT-1) gave `deriveListingFields.js` an explicit District of Columbia guard;
// this module had none, so a D.C. address walked past "District of Columbia" and
// matched the word "Washington" as a US state — a public relabel ~2,300 miles
// wrong, and a wrong `locationState` with it (marketplace MEDIUM-4). Resolving
// the region from the geocoder's own `US-DC` short code, or from the full name
// by equality, removes the guess entirely.

const COARSE_DECIMALS = 1; // 0.1° grid (R5)
const COARSE_FACTOR = 10 ** COARSE_DECIMALS;
const COARSE_TOLERANCE = 1e-9;

const isFiniteNumber = n => typeof n === 'number' && Number.isFinite(n);

/**
 * Fold a place or region name to a comparison key: lower case, accents removed,
 * internal whitespace collapsed, trailing period dropped.
 *
 * 🔴 The accent fold is load-bearing, not cosmetic. Google Places and Mapbox both
 * return `Québec` for French locales, and Canada is a supported market
 * (`publicData.pricingCurrency`, CAD). Without folding, `Montréal, Québec` failed
 * to resolve a region at all and the public location was DELETED — which also
 * flipped the wizard's LOCATION step to incomplete (marketplace MEDIUM-5).
 */
const fold = value =>
  String(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\.$/, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

// name-key → canonical name, built once from the catalogues above.
const REGION_BY_NAME = {};
const REGION_BY_CODE = {};
const REGION_BY_ISO = {};
Object.keys(US_STATES).forEach(code => {
  REGION_BY_NAME[fold(US_STATES[code])] = US_STATES[code];
  REGION_BY_CODE[code] = US_STATES[code];
  REGION_BY_ISO[`US-${code}`] = US_STATES[code];
});
Object.keys(CA_PROVINCES).forEach(code => {
  REGION_BY_NAME[fold(CA_PROVINCES[code])] = CA_PROVINCES[code];
  // No US postal code collides with a Canadian provincial one — verified by the
  // `no code resolves to two regions` test, which asserts the property rather
  // than trusting this comment.
  REGION_BY_CODE[code] = CA_PROVINCES[code];
  REGION_BY_ISO[`CA-${code}`] = CA_PROVINCES[code];
});

/**
 * THE REGION PROPERTY. Resolve one value to a canonical region name, by equality
 * against the catalogues, or null.
 *
 * Accepts an ISO 3166-2 code (`US-TX`, `CA-QC` — what Mapbox returns as
 * `context[].short_code`), a bare two-letter postal code (`TX`, `QC`), or a full
 * name in any case and with or without accents.
 *
 * 🔴 The ISO form is preferred everywhere a structured value is available,
 * because it carries its own country. A BARE `CA` resolves to California here —
 * the only residual ambiguity in the catalogue, since `CA` is also the ISO 3166-1
 * code for Canada. Asked about ONE value, California is the right answer:
 * `Paso Robles, CA` is a shape live listings DO carry, and the previous build's
 * bare `'ca'` country entry shadowed California and deleted the address of every
 * `"City, CA"` listing (tph-api C2). But `Ottawa, ON, CA` is Canada, and this
 * function cannot see the `ON` in front of it — so a TRAILING `CA` is settled by
 * `toPublicAddress`, which can (cold review 2026-09-21, LOW-3). Pinned by test.
 */
export const resolveRegion = value => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (trimmed === '') return null;

  const iso = REGION_BY_ISO[trimmed.toUpperCase()];
  if (iso) return iso;

  const folded = fold(trimmed);
  const byName = REGION_BY_NAME[folded];
  if (byName) return byName;

  // A bare two-letter code, and only a bare one: `TX` resolves, `TX 76086` does
  // not, because a segment carrying a postal code is not a region on its own.
  if (/^[A-Za-z]{2}$/.test(trimmed)) {
    return REGION_BY_CODE[trimmed.toUpperCase()] || null;
  }

  // 🔴 A DOTTED two-letter code — `D.C.`, `N.Y.` — by equality once the dots go
  // (cold review 2026-09-21, LOW-3 / marketplace L1). `Washington, D.C., USA`
  // used to walk straight past `D.C.`, which is neither a name nor a bare code,
  // and resolve the TOWN segment `Washington` as Washington STATE.
  const dotted = trimmed.match(/^([A-Za-z])\.\s?([A-Za-z])\.?$/);
  if (dotted) {
    return REGION_BY_CODE[`${dotted[1]}${dotted[2]}`.toUpperCase()] || null;
  }
  return null;
};

// Which country each canonical region belongs to — the evidence `toPublicAddress`
// checks a region against before it publishes it.
const REGION_COUNTRY = {};
Object.keys(US_STATES).forEach(code => {
  REGION_COUNTRY[US_STATES[code]] = 'US';
});
Object.keys(CA_PROVINCES).forEach(code => {
  REGION_COUNTRY[CA_PROVINCES[code]] = 'CA';
});

// A trailing country segment, by folded equality. 🔴 A bare `CA` is deliberately
// NOT here: it is California's postal code as often as it is Canada's ISO code,
// and `toPublicAddress` settles it from the segment in front of it.
const COUNTRY_BY_NAME = {
  us: 'US',
  usa: 'US',
  'u.s': 'US',
  'u.s.a': 'US',
  'united states': 'US',
  'united states of america': 'US',
  canada: 'CA',
};

/** Read {lat, lng} off a plain object or an SDK LatLng instance. */
const readLatLng = value => {
  if (!value || typeof value !== 'object') return null;
  const { lat, lng } = value;
  return isFiniteNumber(lat) && isFiniteNumber(lng) ? { lat, lng } : null;
};

/**
 * THE COORDINATE PROPERTY. True when the point sits on the 0.1° grid — i.e. it
 * carries no more precision than the grid allows. An absent coordinate is not
 * unsafe, so callers check presence separately.
 */
export const isCoarseCoordinate = value => {
  const latlng = readLatLng(value);
  if (!latlng) return false;
  return [latlng.lat, latlng.lng].every(n => {
    const scaled = n * COARSE_FACTOR;
    return Math.abs(scaled - Math.round(scaled)) < COARSE_TOLERANCE;
  });
};

/** Snap a coordinate onto the 0.1° grid. Deterministic and idempotent. */
export const coarsen = value => {
  const latlng = readLatLng(value);
  if (!latlng) return null;
  const snap = n => {
    const snapped = Math.round(n * COARSE_FACTOR) / COARSE_FACTOR;
    // Round away -0 so the published value never reads as negative zero.
    return snapped === 0 ? 0 : snapped;
  };
  return { lat: snap(latlng.lat), lng: snap(latlng.lng) };
};

const splitSegments = address =>
  String(address)
    .split(',')
    .map(s => s.trim())
    .filter(s => s !== '');

// US ZIP (12345 / 12345-6789), Canadian postal code (A1A 1A1 / A1A1A1), and the
// Canadian forward sortation area on its own (`AB T2P` — a partial postal code).
const US_ZIP = /\b\d{5}(-\d{4})?\b/;
const CA_POSTAL = /\b[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d\b/;
const CA_FSA = /\b[A-Za-z]\d[A-Za-z]\b/;

/**
 * Read one segment of a FORMATTED address, where the geocoder routinely writes
 * the postal code into the same segment as the region — `Texas 78003`,
 * `Alberta T1S 1A1`, `District of Columbia 20001`. Returns the region the
 * segment resolves to, and the country its postal code (if any) belongs to.
 *
 * 🔴 Separate from `resolveRegion` on purpose, and NOT used by the audit
 * predicates. Stripping the postal code is safe when the result must then match
 * the catalogue BY EQUALITY, and unsafe as a way to judge a string somebody else
 * wrote: `isStateOnlyAddress('TX 76086')` must stay false, or a published
 * address could carry a ZIP.
 */
const readSegment = segment => {
  if (typeof segment !== 'string') return { region: null, postalCountry: null };
  let postalCountry = null;
  if (US_ZIP.test(segment)) postalCountry = 'US';
  else if (CA_POSTAL.test(segment) || CA_FSA.test(segment)) postalCountry = 'CA';
  const stripped = segment
    .replace(US_ZIP, ' ')
    .replace(CA_POSTAL, ' ')
    .replace(CA_FSA, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return { region: resolveRegion(stripped), postalCountry, remainder: stripped };
};

// A two-letter region code, bare (`WA`) or dotted (`D.C.`) — never a town.
const isRegionCode = segment =>
  typeof segment === 'string' &&
  (/^[A-Za-z]{2}$/.test(segment.trim()) || /^[A-Za-z]\.\s?[A-Za-z]\.?$/.test(segment.trim()));

/**
 * 🔴 THE STRUCTURED PATH — a fresh geocoder pick.
 *
 * Takes the mapping service's own structured region values and returns the
 * canonical region name. Nothing here slices, trims or inspects the formatted
 * address. Any other key on `structured` (an older bundle's `place`, say) is
 * ignored: the public address is the region and nothing else (scope cut,
 * 2026-09-18).
 *
 * @param {Object} structured
 * @param {string} [structured.region]     the geocoder's `region` context — the full region name
 * @param {string} [structured.regionCode] the geocoder's `short_code` — `US-TX`, `CA-QC`
 * @returns {string|null} the canonical region name, or null when it does not resolve.
 */
export const buildPublicAddress = structured => {
  if (!structured || typeof structured !== 'object') return null;

  // 🔴 A region CODE decides ALONE (cold review 2026-09-22, marketplace M1). It
  // carries its own country, so a code for another country — `GE-TB` (Tbilisi,
  // in Georgia the country), `AU-WA` (Western Australia) — resolves to NOTHING,
  // and the region NAME is not consulted behind it: `Georgia` would resolve to
  // the US state. The name is read only when the service gave no code at all.
  const { regionCode, region } = structured;
  if (typeof regionCode === 'string' && regionCode.trim() !== '') {
    return resolveRegion(regionCode);
  }
  return resolveRegion(region) || null;
};

/** Does a pick carry the geocoder's own region evidence — a code or a name? */
export const hasStructuredRegion = structured =>
  !!structured &&
  typeof structured === 'object' &&
  ['regionCode', 'region'].some(
    key => typeof structured[key] === 'string' && structured[key].trim() !== ''
  );

/**
 * 🔴 THE ONE RESOLVER the save path uses (cold review 2026-09-22, marketplace
 * M1). A pick that carries ANY structured region evidence is decided by
 * `buildPublicAddress` and NEVER falls back to the string. `Perth, WA,
 * Australia` with `AU-WA` used to fall through to the text path, which read
 * `WA` as Washington; `Tbilisi, Georgia` with `GE-TB` fell through to the
 * region name and published the US state. A pick outside the two supported
 * countries publishes NO state. Only a pick with no structured region at all
 * (an older bundle, a service that returned none) is read from the string.
 */
export const resolvePublicAddress = location => {
  if (!location || typeof location !== 'object' || Array.isArray(location)) return null;
  if (hasStructuredRegion(location.structured)) return buildPublicAddress(location.structured);
  return toPublicAddress(location.address);
};

/**
 * THE STRING-ONLY PATH — ruling (d).
 *
 * Used where all we hold is the formatted address and no structured values are
 * available: the tph-api backfill and the events-poller backstop, and the
 * marketplace save path when a pick carries no structured region.
 *
 * 🔴 IT RETURNS THE REGION ALONE, OR NULL. No other segment is ever returned.
 * The previous attempt to decide by wordlist which segment was a town is what
 * published `Windmill Draw, Texas`, `Larkspur Bend, Alberta` and
 * `١٢٣ Juniper Hollow, Texas` to buyers while certifying each one safe.
 */
export const toPublicAddress = address => {
  if (typeof address !== 'string' || address.trim() === '') return null;

  const segments = splitSegments(address);
  let end = segments.length;
  if (end === 0) return null;

  // 🔴 NEVER GUESS A STATE (cold review 2026-09-21, LOW-3). A wrong state is
  // published to buyers and derived into `locationState`, so where the string
  // cannot settle the region unambiguously, NO state is published.
  //
  // First the country, from the trailing segment. A bare trailing `CA` is
  // Canada's ISO code when a CANADIAN region stands in front of it — a province
  // code (`Ottawa, ON, CA`), or a province name with a town before it
  // (`Toronto, Ontario, CA`) — and California when nothing resolvable does
  // (`Paso Robles, CA`). The old walk read every trailing `CA` as California
  // and published Calgary and Ottawa as California.
  //
  // 🔴 `<province name>, CA` ON ITS OWN IS AMBIGUOUS (cold review 2026-09-22,
  // L1): Ontario is a real California city, so `Ontario, CA` is as likely the
  // town as the province. No state is published for it.
  let country = COUNTRY_BY_NAME[fold(segments[end - 1])] || null;
  if (country) {
    end -= 1;
  } else if (end >= 2 && /^ca$/i.test(segments[end - 1])) {
    const before = segments[end - 2];
    const beforeRegion = readSegment(before).region;
    if (beforeRegion && REGION_COUNTRY[beforeRegion] === 'CA') {
      if (end === 2 && REGION_BY_NAME[fold(before)]) return null;
      country = 'CA';
      end -= 1;
    }
  }

  // Then the region: the LAST segment that resolves.
  let region = null;
  let regionIndex = -1;
  for (let i = end - 1; i >= 0; i -= 1) {
    const found = readSegment(segments[i]).region;
    if (found) {
      region = found;
      regionIndex = i;
      break;
    }
  }
  if (!region) return null;
  const regionCountry = REGION_COUNTRY[region];
  if (country && regionCountry !== country) return null;

  // 🔴 It is published only if NOTHING ELSE in the string contradicts it (cold
  // review 2026-09-22, L1). Three contradictions, each a shape that used to
  // publish a wrong state:
  //   - a two-letter region CODE, bare or dotted, that resolves to a DIFFERENT
  //     region: `Seattle, WA, CA, USA` published California. A code is never a
  //     town, so it cannot be explained away as one — a full NAME can be
  //     (`Nevada, Iowa`, `Washington, D.C.`), and is not a contradiction;
  //   - a postal code from the OTHER country, in any segment: `Austin, TX,
  //     K1A 0B1`. A postal code counts only when its segment is nothing else,
  //     or a region plus the code — `12345 Main St` is a house number;
  //   - a postal code whose OWN region — the one sharing its segment, or the
  //     segment right before it — is not the region found: `Maine, 04101, CA`
  //     published California.
  for (let i = 0; i < end; i += 1) {
    const { region: other, postalCountry, remainder } = readSegment(segments[i]);
    const postal = postalCountry && (other || remainder === '') ? postalCountry : null;
    if (postal && postal !== regionCountry) return null;
    if (i === regionIndex) continue;
    if (other && other !== region && isRegionCode(segments[i])) return null;
    if (postal) {
      const postalRegion = other || (i > 0 ? readSegment(segments[i - 1]).region : null);
      if (postalRegion && postalRegion !== region) return null;
    }
  }
  return region;
};

/**
 * 🔴 PUBLIC-DATA KEYS THAT MAY HOLD NO VALUE AT ALL (rulings R14 + R15,
 * 2026-09-21). Both were checked against the PAYLOAD, not the pages: no buyer-
 * or seller-facing surface displays either, and both are delivered to every
 * buyer's browser.
 *
 *   `city`         the town, written by the legacy import (tph-api
 *                  `scripts/migration/generateEdn.js`). No reader anywhere.
 *   `contactPhone` the per-listing inquiry routing number. tph-api ROUTES on it
 *                  (`sellerPhone.js`), so it moves to `privateData.contactPhone`
 *                  — it is never simply deleted.
 *
 * The save choke point never SENDS either key (a `null` from the browser would
 * destroy the only copy before the server backstop can move it privately); the
 * backstop and the backfill move the stored value into privateData and clear the
 * public one.
 */
export const PRIVATE_ONLY_PUBLIC_DATA_KEYS = ['city', 'contactPhone'];

// Every canonical region name, exactly as the catalogues spell it.
const CANONICAL_REGION_NAMES = new Set([
  ...Object.values(US_STATES),
  ...Object.values(CA_PROVINCES),
]);

/**
 * 🔴 THE ADDRESS PROPERTY (scope cut, 2026-09-18). A safe public address is a
 * canonical state or province name AND NOTHING ELSE.
 *
 * Membership by EQUALITY in the catalogue's own spelling — not "resolves to a
 * region", which would also accept `TX`, `texas` or `Québec` — so the only
 * strings that pass are the ones the constructors above emit. There is no
 * second segment to judge, so there is nothing here a string cannot certify.
 */
export const isStateOnlyAddress = address =>
  typeof address === 'string' && CANONICAL_REGION_NAMES.has(address);

/**
 * THE PAYLOAD PROPERTY — what every acceptance criterion means by "safe".
 *
 * Holds when the geolocation is absent or coarse, and publicData.location is
 * absent, or carries an address that is absent or `isStateOnlyAddress`, and no
 * other key (no `building`, no nested coordinates).
 *
 * 🔴 IT FAILS CLOSED. A non-object, a string where an object belongs, an array —
 * all return false. The first build returned `true` for a non-object, so
 * `isPublicLocationSafe('4821 Juniper Hollow Lane, Bandera, TX')` was `true`
 * (marketplace L6). Every other guard in this file fails closed; this one does now.
 *
 * 🔴 AND IT READS THE WHOLE PAYLOAD, NOT ONE KEY (operator rulings R14, R15,
 * 2026-09-21). The first version looked only at `geolocation` and
 * `publicData.location`, while the town sat in `publicData.city` on 307 of 576
 * published listings and a routing phone number in `publicData.contactPhone` on
 * 44 — delivered to every buyer's browser. A census built on this predicate
 * reported "0 unsafe" with the town still public: the CLAUDE.md class, asserting
 * the key usually called `location` instead of the property "nothing
 * seller-locating in the public payload". Any value in a
 * `PRIVATE_ONLY_PUBLIC_DATA_KEYS` key now fails it.
 *
 * @param {Object} attributes a listing's `attributes` or an outgoing save payload
 */
export const isPublicLocationSafe = attributes => {
  // Fail closed (L6): nothing we cannot read is "safe".
  if (!attributes || typeof attributes !== 'object' || Array.isArray(attributes)) return false;

  const { geolocation, publicData } = attributes;
  const hasGeo = geolocation !== null && geolocation !== undefined;
  if (hasGeo && !isCoarseCoordinate(geolocation)) return false;

  const hasPublicData = publicData !== null && publicData !== undefined;
  if (hasPublicData && (typeof publicData !== 'object' || Array.isArray(publicData))) {
    return false;
  }

  if (
    hasPublicData &&
    PRIVATE_ONLY_PUBLIC_DATA_KEYS.some(
      key => publicData[key] !== undefined && publicData[key] !== null
    )
  ) {
    return false;
  }

  const location = publicData && publicData.location;
  if (location === undefined || location === null) return true;
  if (typeof location !== 'object' || Array.isArray(location)) return false;

  const keys = Object.keys(location).filter(k => location[k] !== undefined);
  if (keys.some(k => k !== 'address')) return false;

  const { address } = location;
  if (address === undefined || address === null) return true;
  return isStateOnlyAddress(address);
};

/**
 * Rebuild a coordinate in the same class the caller handed us. The wizard's
 * geolocation is an SDK `LatLng` instance and the Sharetribe SDK serializes it
 * BY TYPE — a plain object goes on the wire as a transit map, an SDK `LatLng`
 * goes as the `~#geo` tag. tph-api's C1 was exactly this: two write paths called
 * `coarsen` directly and never reached this function.
 */
export const rebuildLatLng = (original, coarse) => {
  if (!coarse) return coarse;
  const Ctor = original && typeof original === 'object' ? original.constructor : null;
  if (Ctor && Ctor !== Object) {
    try {
      return new Ctor(coarse.lat, coarse.lng);
    } catch (_err) {
      // A constructor we cannot call with (lat, lng) — fall through rather than
      // dropping the coordinate.
    }
  }
  // 🔴 No usable class: stamp the SDK's own type marker instead of handing back
  // a bare object. The transit write handlers are keyed by CLASS IDENTITY, so a
  // plain `{lat, lng}` falls to the default map handler and goes on the wire as
  // `["^ ","~:lat",30.4,"~:lng",-97.7]` rather than `["~#geo",[30.4,-97.7]]`.
  // `_sdkType` is the SDK's documented escape hatch for exactly this, and the
  // wire-form test asserts the SERIALIZED BODY, not this comment.
  return { lat: coarse.lat, lng: coarse.lng, _sdkType: 'LatLng' };
};

/**
 * THE CHOKE POINT. Returns a copy of an outgoing save payload whose public
 * location is one we CONSTRUCTED.
 *
 * - `geolocation` is snapped to the grid, keeping its original class. A value
 *   that cannot be read as a coordinate is CLEARED, never kept.
 * - `publicData.location` is reduced to `{ address }` — the REGION ALONE —
 *   resolved from `payload.publicData.location.structured` when the panel
 *   supplied it (a fresh geocoder pick), and otherwise from the string path.
 * - `structured` never survives into the payload: it is an input to this
 *   function, not a public key. `isPublicLocationSafe` rejects any key but
 *   `address`, and that rejection is the test that proves it.
 * - `publicData.city` and `publicData.contactPhone` (`PRIVATE_ONLY_PUBLIC_DATA_KEYS`)
 *   are never SENT (R14, R15). They are omitted rather than nulled on purpose:
 *   the browser holds no private copy of either, so a `null` here would destroy
 *   the only one before the server backstop moves it into privateData. Every
 *   panel spreads the page-load snapshot forward, so without this each save
 *   re-published the town (cold review 2026-09-21, H1).
 * - `privateData` is NEVER touched. The panel is what writes the exact values
 *   there on a fresh pick (AC3); this function must not invent them.
 * - A payload with none of these keys passes through unchanged — which is what
 *   makes a title edit harmless (marketplace HIGH-2).
 *
 * 🔴 WHEN NOTHING RESOLVES, `publicData.location` IS SENT AS `null` — CLEARED,
 * NEVER OMITTED (operator ruling R13, 2026-09-19). Sharetribe merges publicData
 * shallowly, so an omitted key leaves the STORED value in place — on a listing
 * that predates the backfill, the old exact street address, still public. The
 * server backstop then saw an unsafe resource and copied that old address over
 * the seller's fresh private pick (cold review 2026-09-21, M1). The same holds
 * in the fail-closed branch below.
 *
 * What clearing costs, stated as the operator asked: the wizard's LOCATION
 * completion check is `!!(geolocation && publicData?.location?.address)`
 * (`EditListingWizard.js:286`), so the step reads as INCOMPLETE for such a
 * listing, and `deriveLocationState` writes no `locationState`. Both are pinned
 * by test. Publishing an unresolved segment instead is not an option (ruling a).
 *
 * Never throws: a save must not fail because sanitizing failed. If anything is
 * unexpected the location keys are cleared rather than published.
 */
export const sanitizeListingLocation = payload => {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return payload;

  const own = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key);
  const hasGeolocation = own(payload, 'geolocation');
  const publicDataIsObject =
    !!payload.publicData &&
    typeof payload.publicData === 'object' &&
    !Array.isArray(payload.publicData);
  const hasLocation = publicDataIsObject && own(payload.publicData, 'location');
  const hasPrivateOnly =
    publicDataIsObject && PRIVATE_ONLY_PUBLIC_DATA_KEYS.some(key => own(payload.publicData, key));

  if (!hasGeolocation && !hasLocation && !hasPrivateOnly) return payload;

  try {
    const next = { ...payload };

    if (hasGeolocation && payload.geolocation !== null && payload.geolocation !== undefined) {
      const coarse = coarsen(payload.geolocation);
      next.geolocation = coarse ? rebuildLatLng(payload.geolocation, coarse) : null;
    }

    if (hasLocation || hasPrivateOnly) {
      const publicData = { ...payload.publicData };
      PRIVATE_ONLY_PUBLIC_DATA_KEYS.forEach(key => {
        delete publicData[key];
      });

      if (hasLocation) {
        const location = publicData.location;
        const isObject = location && typeof location === 'object' && !Array.isArray(location);
        // Structured first (ruling b), string path ONLY when there is no
        // structured region at all (ruling d; M1 2026-09-22 — never a fallback
        // from a foreign region code).
        const address = isObject ? resolvePublicAddress(location) : null;
        // R13 — cleared, never omitted.
        publicData.location = address ? { address } : null;
      }
      next.publicData = publicData;
    }

    return next;
  } catch (_err) {
    // Fail closed: clear the location keys rather than let an exact value through.
    //
    // 🔴 THE FAIL-CLOSED BRANCH MUST NOT ITSELF THROW. Spreading
    // `payload.publicData` re-reads every enumerable property, so whatever threw
    // on the way in — a getter, a proxy — throws again here and the exception
    // escapes into the save. Copy key by key, skipping `location` (which is
    // cleared below), the private-only keys (never sent), and any key that throws.
    const next = { ...payload };
    if (hasGeolocation) next.geolocation = null;
    if (hasLocation || hasPrivateOnly) {
      const publicData = {};
      Object.keys(payload.publicData).forEach(key => {
        if (key === 'location' || PRIVATE_ONLY_PUBLIC_DATA_KEYS.includes(key)) return;
        try {
          publicData[key] = payload.publicData[key];
        } catch (_readErr) {
          // A key we cannot read is a key we do not carry forward.
        }
      });
      // R13 — the error branch clears too. Omitting here left the stored exact
      // address public exactly as the unresolved fresh pick did (M1).
      if (hasLocation) publicData.location = null;
      next.publicData = publicData;
    }
    return next;
  }
};
